import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { sendLovableEmail } from "@lovable.dev/email-js";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const ChecklistLeadSchema = z.object({
  email: z.string().email().max(200),
  name: z.string().min(1).max(120).optional().nullable(),
  company: z.string().max(200).optional().nullable(),
  role: z.string().max(120).optional().nullable(),
  checklist_slug: z.string().min(1).max(120),
  locale: z.enum(["de", "en"]).default("de"),
  marketing_consent: z.literal(true, {
    errorMap: () => ({ message: "consent_required" }),
  }),
  source: z.enum(["email-magnet", "direct"]).default("email-magnet"),
  website: z.string().max(200).optional().default(""), // honeypot
});

export type ChecklistLeadInput = z.infer<typeof ChecklistLeadSchema>;

const SITE_URL = "https://www.calvarius.com";

const DOWNLOAD_PATHS: Record<string, { de: string; en: string }> = {
  "performance-marketing-setup": {
    de: "/static/checklists/performance-marketing-setup-de.pdf",
    en: "/static/checklists/performance-marketing-setup-en.pdf",
  },
};

const FROM = "Calvarius <hello@notify.calvarius.com>";
const SENDER_DOMAIN = "notify.calvarius.com";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function emailTemplateDE(downloadUrl: string, name: string | null): { subject: string; html: string; text: string } {
  const safeName = name ? escapeHtml(name) : null;
  const greeting = name ? `Guten Tag ${name},` : "Guten Tag,";
  const greetingHtml = safeName ? `Guten Tag ${safeName},` : "Guten Tag,";
  const subject = "Ihre Performance-Marketing-Checkliste";
  const text = `${greeting}

vielen Dank für Ihr Interesse an der Calvarius-Checkliste „Performance-Marketing-Setup für KMU".

Die Checkliste finden Sie hier zum Download:
${downloadUrl}

Sie ist methodisch substantiell, aber kein Ersatz für individuelle Betrachtung. Wenn Sie die Anwendung auf Ihren konkreten Fall besprechen wollen, ist ein kostenfreies Strategiegespräch der direkte Weg:
${SITE_URL}/kontakt?betreff=strategiegespraech

Methodische Grüße
Calvarius
`;
  const html = `<!doctype html><html lang="de"><body style="font-family:Georgia,serif;color:#111;max-width:560px;margin:0 auto;padding:24px;line-height:1.6;">
<p>${greetingHtml}</p>
<p>vielen Dank für Ihr Interesse an der Calvarius-Checkliste <em>„Performance-Marketing-Setup für KMU"</em>.</p>
<p><a href="${downloadUrl}" style="display:inline-block;background:#c9f24a;color:#111;padding:12px 22px;text-decoration:none;font-family:ui-monospace,monospace;font-size:13px;letter-spacing:0.04em;text-transform:uppercase;border-radius:6px;">Checkliste herunterladen (PDF) →</a></p>
<p style="color:#444;">Die Checkliste ist methodisch substantiell, aber kein Ersatz für individuelle Betrachtung. Wenn Sie die Anwendung auf Ihren konkreten Fall besprechen wollen, ist ein kostenfreies Strategiegespräch der direkte Weg:</p>
<p><a href="${SITE_URL}/kontakt?betreff=strategiegespraech" style="color:#0a6abf;">Strategiegespräch anfragen →</a></p>
<p style="margin-top:32px;color:#666;font-size:13px;">Methodische Grüße<br/>Calvarius</p>
</body></html>`;
  return { subject, html, text };
}

function emailTemplateEN(downloadUrl: string, name: string | null): { subject: string; html: string; text: string } {
  const safeName = name ? escapeHtml(name) : null;
  const greeting = name ? `Hello ${name},` : "Hello,";
  const greetingHtml = safeName ? `Hello ${safeName},` : "Hello,";
  const subject = "Your performance marketing checklist";
  const text = `${greeting}

thank you for your interest in the Calvarius checklist "Performance marketing setup for SMBs".

You can download the checklist here:
${downloadUrl}

It is methodically substantive, but not a replacement for an individual assessment. If you want to discuss application to your specific case, a free strategy call is the most direct path:
${SITE_URL}/en/contact?subject=strategy-meeting

Methodical regards
Calvarius
`;
  const html = `<!doctype html><html lang="en"><body style="font-family:Georgia,serif;color:#111;max-width:560px;margin:0 auto;padding:24px;line-height:1.6;">
<p>${greetingHtml}</p>
<p>thank you for your interest in the Calvarius checklist <em>"Performance marketing setup for SMBs"</em>.</p>
<p><a href="${downloadUrl}" style="display:inline-block;background:#c9f24a;color:#111;padding:12px 22px;text-decoration:none;font-family:ui-monospace,monospace;font-size:13px;letter-spacing:0.04em;text-transform:uppercase;border-radius:6px;">Download checklist (PDF) →</a></p>
<p style="color:#444;">The checklist is methodically substantive, but not a replacement for an individual assessment. If you want to discuss application to your specific case, a free strategy call is the most direct path:</p>
<p><a href="${SITE_URL}/en/contact?subject=strategy-meeting" style="color:#0a6abf;">Request a strategy call →</a></p>
<p style="margin-top:32px;color:#666;font-size:13px;">Methodical regards<br/>Calvarius</p>
</body></html>`;
  return { subject, html, text };
}

export const submitChecklistLead = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => ChecklistLeadSchema.parse(data))
  .handler(async ({ data }) => {
    // Honeypot — silently accept but skip side effects
    if (data.website && data.website.trim().length > 0) {
      return { ok: true as const, downloadUrl: null, skipped: true };
    }

    const req = getRequest();
    const ip =
      req?.headers.get("cf-connecting-ip") ??
      req?.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
      null;
    const userAgent = req?.headers.get("user-agent") ?? null;

    const paths = DOWNLOAD_PATHS[data.checklist_slug];
    const downloadPath = paths ? paths[data.locale] : null;
    const downloadUrl = downloadPath ? `${SITE_URL}${downloadPath}` : null;

    // Server-side IP rate limit: 20 / day / IP
    if (ip) {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { count } = await supabaseAdmin
        .from("checklist_leads")
        .select("id", { count: "exact", head: true })
        .eq("ip", ip)
        .gte("created_at", since);
      if ((count ?? 0) >= 20) {
        throw new Error("rate_limited");
      }
    }

    const { error: insertError } = await supabaseAdmin.from("checklist_leads").insert({
      email: data.email,
      name: data.name ?? null,
      company: data.company ?? null,
      role: data.role ?? null,
      checklist_slug: data.checklist_slug,
      locale: data.locale,
      marketing_consent: data.marketing_consent,
      ip,
      user_agent: userAgent,
    });

    if (insertError) {
      console.error("[checklist] insert failed", insertError);
      throw new Error("Speichern fehlgeschlagen. Bitte erneut versuchen.");
    }

    // Only send email for explicit email-magnet path
    if (data.source === "email-magnet" && downloadUrl) {
      const apiKey = process.env.LOVABLE_API_KEY;
      if (apiKey) {
        const tpl = data.locale === "de"
          ? emailTemplateDE(downloadUrl, data.name ?? null)
          : emailTemplateEN(downloadUrl, data.name ?? null);
        const messageId = `checklist-${data.checklist_slug}-${crypto.randomUUID()}`;
        try {
          await sendLovableEmail(
            {
              to: data.email,
              from: FROM,
              sender_domain: SENDER_DOMAIN,
              subject: tpl.subject,
              html: tpl.html,
              text: tpl.text,
              purpose: "transactional",
              label: "checklist_delivery",
              idempotency_key: `checklist-${data.email}-${new Date().toISOString().slice(0, 10)}`,
              message_id: messageId,
              unsubscribe_token: crypto.randomUUID(),
            },
            { apiKey },
          );
          await supabaseAdmin.from("email_send_log").insert({
            message_id: messageId,
            template_name: "checklist_delivery",
            recipient_email: data.email,
            status: "sent",
            metadata: { checklist_slug: data.checklist_slug, locale: data.locale },
          });
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : String(err);
          console.error("[checklist] email send failed", errMsg);
          await supabaseAdmin.from("email_send_log").insert({
            message_id: messageId,
            template_name: "checklist_delivery",
            recipient_email: data.email,
            status: "failed",
            error_message: errMsg,
            metadata: { checklist_slug: data.checklist_slug, locale: data.locale },
          });
          // Still return downloadUrl so user gets the file via UI fallback
        }
      }
    }

    return { ok: true as const, downloadUrl: downloadPath };
  });
